<template>
  <el-container class="preview">
    <el-header>
      <share-header
        v-if="!isLoading"
        :isOwner="isOwner"
        :file="file"
        :user="user"
        :shareId="shareId"
        :isIllegalFile="isIllegalFile"
        :hasAuthority="user.userId"
        @reportIllegalFile="handleReportIllegalFile"
      />
    </el-header>
    <password v-if="needPassword" :shareId="shareId" @pass="pass"></password>
    <el-main v-else-if="!isIllegalFile" :class="{ 'preview-mhe': file.contentType === 'mhe' }">
      <preview-main
        @showLogin="handleShowLogin"
        class="preview-main"
        :showOpenAppTip="showOpenAppTip"
        :file="file"
        :isOwner="isOwner"
        :isLoading="isLoading"
        :user="user"
      />
    </el-main>
    <component class="dialog-container" :is="dialogInfo.component"></component>
    <illegal-file-report
      v-if="file && file.itemId"
      ref="illegalFileReport"
      :is-illegal-file="isIllegalFile"
      :file-detail="file"
      :user-id="user && user.userId"
      @after-report-success="afterReportSuccess"
    />
  </el-container>
</template>

<script>
import Vue from 'vue';
import { Cookie } from '@utils';
import { AIRDISK } from '@services';
import PreviewMain from './PreviewMain/PreviewMain';
import InitDialog from './InitDialog/InitDialog';
import LoginDialog from '@components/PreviewHeader/LoginDialog/LoginDialog';
import NotAuthDialog from './NotAuthDialog/NotAuthDialog';
import { getUrlParams } from '@business/CreateDoc/utils/mindeditorUtils';
import mhcActivityMixins from '@mixins/activity/mhcActivity.mixins';
import IllegalFileReportMixins from '@mixins/illegalFileReport.mixins';
import clientDiff from '@client-diff';
import IllegalFileReport from '@components/IllegalFileReport/IllegalFileReport.vue';
import Password from '@components/Password/Password.vue';
import Constants from '@constants';

import { store as electronStore } from '@maxhub/super-electron';

const shareId = getUrlParams('shareId');
const teamId = getUrlParams('teamId');
const IS_ELECTRON = clientDiff.IS_ELECTRON;

const { PRIVATE_ALL_CAN_EDIT, TEAM_CAN_EDIT, TEAM_ALL_CAN_EDIT } = Constants.Common.shareType;

export default {
  name: 'Share',
  mixins: [mhcActivityMixins, IllegalFileReportMixins],
  components: { PreviewMain, IllegalFileReport, Password },
  data() {
    return {
      IS_ELECTRON,
      showTip: false,
      needPassword: false,
      showOpenAppTip: false,
      shareId: shareId,
      file: {},
      user: {},
      isOwner: false,
      isLoading: true,
      hasShowInitDialog: false,
      hasShowOpenTip: false
    };
  },
  computed: {
    dialogInfo() {
      return Vue.store.state.dialogInfo;
    }
  },
  methods: {
    async getFileDetail() {
      try {
        const { user } = this;
        let shareRes = null;
        if (user.userId) {
          shareRes = await AIRDISK.Airdisk.getFileByShareIdAuth(shareId);
          const fileId = this.file.itemId || shareRes?.data?.itemId;
          // 修复团队内可查看文件，非团队成员请求接口返回403，后端也不会返回itemId继续请求checkIfOwner 导致抛错误代码没往下走 的问题
          if (fileId) {
            const ownerRes = await AIRDISK.Airdisk.checkIfOwner(fileId);
            this.isOwner = ownerRes.owner;
          }
        } else {
          const { value: endpointId } = await electronStore.getState('endpointId');
          shareRes = await AIRDISK.Airdisk.getFileByShareId(shareId, null, endpointId);
          // 展示弹窗
          if (this.hasShowInitDialog === false) {
            this.hasShowInitDialog = true;
            Vue.MXDialog.show(InitDialog, {
              file: this.file
            })
              .then(res => {
                setTimeout(() => {
                  Vue.MXDialog.show(LoginDialog);
                }, 400);
              })
              .catch(res => {
                this.showOpenTip();
              });
          }
        }

        if (shareRes.code === 404) {
          this.file = { code: 404 };
          this.isLoading = false;
          return;
        } else if (shareRes.code === 403) {
          shareRes.data.status = 2;
        }
        shareRes.data.code = shareRes.code;
        this.file = shareRes.data;
        // 如果是没有访问权限的文件，则弹出提示框没有访问的权限
        if ((shareRes.code === 404 || shareRes.code === 403) && this.file.teamFile) {
          Vue.MXDialog.show(NotAuthDialog, {
            file: this.file
          });
        }

        const fileStatus = this.file.status;
        // 能编辑的文件
        const isEditeFile =
          fileStatus === PRIVATE_ALL_CAN_EDIT.value ||
          fileStatus === TEAM_CAN_EDIT.value ||
          fileStatus === TEAM_ALL_CAN_EDIT.value;

        // mhc && 可编辑 && 登录用户
        const isEditorPage = this.file.contentType === 'mhc' && (this.isOwner || (this.user.userId && isEditeFile));

        if (isEditorPage) {
          const itemId = this.file.itemId;
          const editorHref = `/editor.html#/create-doc/?baseWindowName=${itemId}&shareId=${shareId}&fileId=${itemId}&teamId=${teamId}`;
          window.location.href = editorHref;
        } else {
          this.showOpenTip();
        }
        // 检查文件的合法性
        this.checkIsIllegalFile(this.file);
      } catch (e) {
        const needPasswordCode = [4011000, 4001000, 4001003];
        if (needPasswordCode.includes(e?.response?.data?.code)) {
          this.needPassword = true;
        }
      }

      this.isLoading = false;
    },
    handleShowLogin() {
      Vue.MXDialog.show(LoginDialog);
    },
    handleReportIllegalFile() {
      this.$refs.illegalFileReport.showFileReportDialog();
    },
    afterReportSuccess() {
      // 举报非法文件成功后，重新获取文件详情，以显示最新的文件状态
      this.getFileDetail();
    },
    showOpenTip() {
      if (this.hasShowOpenTip === true) {
        return;
      }
      this.hasShowOpenTip = true;
      setTimeout(() => {
        this.$bus.$emit('show-open-tips');
      }, 0);
    },
    pass(val) {
      this.needPassword = false;
      this.isLoading = true;
      this.getFileDetail();
    }
  },
  created() {
    // 获取用户信息，判断是否登录
    this.isLoading = true;
    AIRDISK.Airdisk.getUserInfo()
      .then(async res => {
        res.avatar = res.avatar || res.headimgurl;
        Vue.store.commit(Vue.VUEX_TYPES.SET_ACCOUNT_USERINFO, res);
        Vue.store.commit(Vue.VUEX_TYPES.SET_USERINFO, res);
        this.user = res;
        let userInfo = JSON.stringify(res);
        localStorage.setItem('userInfo', userInfo);
        this.$fa.setUser({
          id: res.userId
        });
        this.IS_ELECTRON &&
          (await Cookie.set(
            'userInfo',
            encodeURIComponent(userInfo),
            parseInt(new Date().valueOf() / 1000) + 360 * 24 * 60 * 60
          ));
        this.getFileDetail();
      })
      .catch(e => {
        this.getFileDetail();
      });
  }
};
</script>

<style lang="scss" scoped>
@import './Share.scss';
</style>
<style lang="scss">
html {
  background: #eff0f4;
}
* {
  user-select: auto !important;
}
</style>
